<section class="hd-streams-cheat-sheet-page" [class.is-split]="splitForPrint()">
  <!-- Controls -->
  <e-card2 class="hd-streams-cheat-sheet-page__controls-card" padding="2">
    <div class="hd-streams-cheat-sheet-page__controls" aria-label="Stromen cheat sheet controls">
      <e-hd-content-language-picker></e-hd-content-language-picker>

      <span class="hd-streams-cheat-sheet-page__divider"></span>

      <button
        eBtn
        size="sm"
        squared
        type="button"
        icon="columns"
        [color]="splitForPrint() ? 'highlight' : 'primary-75'"
        [iconColor]="splitForPrint() ? 'primary-75' : undefined"
        tooltip="Splitsen in 2 bladen voor print"
        (click)="toggleSplitForPrint()"
      ></button>

      <button
        eBtn
        size="sm"
        squared
        type="button"
        icon="print"
        tooltip="Print de cheat sheet"
        (click)="print()"
      ></button>
    </div>
  </e-card2>

  <!-- Printable area -->
  <div class="hd-streams-cheat-sheet-page__sheets">
    <!-- Sheet 1 -->
    <div class="hd-streams-cheat-sheet-page__sheet">
      <h2 class="hd-streams-cheat-sheet-page__sheet-title">
        {{ cheatSheet().pageTitle }}<span class="hd-streams-cheat-sheet-page__sheet-no"> 1/2</span>
      </h2>

      @for (row of sheet1Rows(); track row.key) {
        <ng-container [ngTemplateOutlet]="streamRow" [ngTemplateOutletContext]="{ $implicit: row }"></ng-container>
      }
    </div>

    <!-- Sheet 2 -->
    <div class="hd-streams-cheat-sheet-page__sheet">
      <h2 class="hd-streams-cheat-sheet-page__sheet-title">
        {{ cheatSheet().pageTitle }}<span class="hd-streams-cheat-sheet-page__sheet-no"> 2/2</span>
      </h2>

      @for (row of sheet2Rows(); track row.key) {
        <ng-container [ngTemplateOutlet]="streamRow" [ngTemplateOutletContext]="{ $implicit: row }"></ng-container>
      }

      <!-- Legend -->
      <article class="streams-legend">
        <h3 class="streams-legend__title">{{ cheatSheet().legend.title }}</h3>
        <div class="streams-legend__cols">
          <div class="streams-legend__col">
            <p class="streams-legend__text" [innerHTML]="cheatSheet().legend.intro"></p>
          </div>

          <div class="streams-legend__col">
            <p class="streams-legend__text" [innerHTML]="cheatSheet().legend.centersText"></p>

            <div class="streams-legend__block">
              <div class="streams-legend__block-title">{{ cheatSheet().legend.markersTitle }}</div>
              <ul class="streams-legend__markers">
                @for (marker of cheatSheet().legend.markers; track marker.marker) {
                  <li class="streams-legend__marker-item">
                    <span class="streams-legend__marker" [style.background-color]="marker.color">{{ marker.marker }}</span>
                    {{ marker.role }}
                  </li>
                }
              </ul>
            </div>
          </div>

          <div class="streams-legend__col">
            <div class="streams-legend__block">
              <div class="streams-legend__block-title">{{ cheatSheet().legend.circuitsTitle }}</div>
              <p class="streams-legend__text">{{ cheatSheet().legend.circuitsIntro }}</p>
              <ul class="streams-legend__circuits">
                @for (circuit of cheatSheet().legend.circuits; track circuit.label) {
                  <li class="streams-legend__circuit-item" [style.color]="circuit.color">
                    <span class="streams-legend__swatch" [style.background-color]="circuit.color"></span>
                    {{ circuit.label }}
                  </li>
                }
              </ul>
            </div>

            <div class="streams-legend__block">
              <div class="streams-legend__block-title">{{ cheatSheet().legend.flowTitle }}</div>
              <p class="streams-legend__text">{{ cheatSheet().legend.flow }}</p>
            </div>
          </div>
        </div>
      </article>
    </div>
  </div>
</section>

<!-- ───────────────────────── Stream row template ───────────────────────── -->
<ng-template #streamRow let-row>
  <article class="stream-row" [style.--family-color]="row.familyColor">
    <!-- Flow column: header + stappen + stroom-visual (linker kolom in de Figma) -->
    <div class="stream-flow">
      <header class="stream-row__header">
        <div class="stream-row__header-line">
          <span class="stream-row__awareness">{{ row.awarenessName }}</span>
          <span class="stream-row__label">{{ row.streamLabel }}:</span>
        </div>
        <span class="stream-row__name">{{ row.streamName }}</span>
      </header>

      <div class="stream-flow__content">
        <div class="stream-flow__summary">
          <div class="stream-flow__channels">{{ row.flowChannels }}</div>
          <ol class="stream-flow__steps">
            @for (step of row.flow; track step.marker) {
              <li class="stream-flow__step">
                <span class="stream-flow__marker" [style.background-color]="step.markerColor">{{ step.marker }}</span>
                <span class="stream-flow__step-text">
                  <span class="stream-flow__gate">{{ step.gate }} -</span>
                  <span class="stream-flow__name">{{ step.name }}</span>
                  <span class="stream-flow__note">{{ step.note }}</span>
                  @if (step.unrest) {
                    <span class="stream-flow__unrest">
                      <span class="stream-flow__unrest-label">{{ row.unrestLabel }}:</span> {{ step.unrest }}
                    </span>
                  }
                </span>
              </li>
            }
          </ol>
          @if (row.flowNote) {
            <p class="stream-flow__note-extra">{{ row.flowNote }}</p>
          }
        </div>
        <img class="stream-flow__image" [src]="row.flowImage" [alt]="row.streamName" loading="lazy" />
      </div>
    </div>

    <!-- Channel cards -->
    @for (channel of row.channels; track channel.key) {
      <article class="channel-card">
        <header class="channel-card__header">
          <span class="channel-card__gates">
            <span>{{ channel.gateA }}</span>
            <span class="channel-card__gates-dash">-</span>
            <span>{{ channel.gateB }}</span>
          </span>
          <span class="channel-card__title">{{ channel.title }}</span>
        </header>

        <div class="channel-card__meta">
          @if (channel.type) {
            <span class="channel-card__type">{{ channel.type }}</span>
          }
          <span class="channel-card__circuit" [style.color]="row.familyColor">{{ channel.circuit }}</span>
        </div>

        <div class="channel-card__body">
          <div class="channel-card__gate-info">
            @for (block of channel.gateBlocks; track block.gate) {
              <div class="gate-block">
                <div class="gate-block__title">{{ block.gate }}: {{ block.title }}</div>
                <div class="gate-block__desc">{{ block.desc }}</div>
                @if (block.spectrum && includeNonHdFeatures()) {
                  <div class="gate-block__spectrum">{{ block.spectrum }}</div>
                }
              </div>
            }
          </div>
          <div class="channel-card__side">
            <img class="channel-card__image" [src]="channel.image" [alt]="channel.title" loading="lazy" />
            @if (channel.english) {
              <span class="channel-card__english">{{ channel.english }}</span>
            }
          </div>
        </div>
      </article>
    }
  </article>
</ng-template>
